// The signed-in user and server config. A load shows the app at once for
// the account last signed in on this browser, when one is remembered, and
// asks `/api/me` behind it (see `load`); otherwise the app waits for the
// answer.
import { config, isConfigured, me, SETUP_PATH, type AppConfig, type Me } from '../api/server';
import { clearUserData, idbEntries, setDbUser, settledWithin } from '../api/db';
import { api, beginSignOut, cancelSignOut, holdSignedOutUntil } from '../api/http';
import { HttpError } from '../api/cache';
import { failureOf, type Failure } from '../api/failure';
import type { OperatorLinkUrls } from '../logic/operatorLinks';
import { selectIdentity } from '../api/identity';
import { flushOpenMarks, replayPendingMarks } from './marks.svelte';
import { claimLocalState, clearLocalUserState } from './prefs.svelte';
import { dashboardStorage } from './dashCache';
import { clearForSignOut } from '../api/relay';
import { MOCK } from '../mock/flag';
import { Retrier } from '../logic/backoff';

/** How long sign-out waits for queued review marks to reach the server. */
const MARKS_FLUSH_WAIT_MS = 3000;

const SESSION_SIGNAL = 'changelist:session-identity';

/** The operator's links from the last `/api/config` read (not user data; kept across sign-outs). */
const OPERATOR_LINKS_KEY = 'changelist:operator-links';

function rememberedLinks(): OperatorLinkUrls | null {
  try {
    const raw = localStorage.getItem(OPERATOR_LINKS_KEY);
    return raw ? (JSON.parse(raw) as OperatorLinkUrls) : null;
  } catch {
    return null;
  }
}

function rememberLinks(links: OperatorLinkUrls | null) {
  try {
    if (links) localStorage.setItem(OPERATOR_LINKS_KEY, JSON.stringify(links));
    else localStorage.removeItem(OPERATOR_LINKS_KEY);
  } catch { /* storage unavailable */ }
}

/**
 * The account last signed in on this browser: its `Me` (ID, login, name,
 * avatar URL, whether an operator), nothing secret. A load shows its app
 * before `/api/me` answers; sign-out, and an answer naming nobody, forget
 * it.
 */
const LAST_USER_KEY = 'changelist:last-user';

/** The remembered account; null when there is none, or what is kept is not one (then the load waits, as without). */
function lastUser(): Me | null {
  try {
    const raw = localStorage.getItem(LAST_USER_KEY);
    const u: unknown = raw ? JSON.parse(raw) : null;
    if (typeof u !== 'object' || u === null) return null;
    const { id, login, name, avatar_url, operator } = u as Record<string, unknown>;
    if (!Number.isSafeInteger(id) || typeof login !== 'string' || !login || typeof avatar_url !== 'string') return null;
    if ((name !== null && typeof name !== 'string') || (operator !== undefined && typeof operator !== 'boolean')) return null;
    return { id: id as number, login, name, avatar_url, operator: operator === true };
  } catch {
    return null;
  }
}

function rememberUser(u: Me) {
  const { id, login, name, avatar_url, operator } = u;
  try { localStorage.setItem(LAST_USER_KEY, JSON.stringify({ id, login, name, avatar_url, operator: operator === true })); } catch { /* storage unavailable */ }
}

/** Forget the remembered account: any, or only when it is `id` (another tab may have remembered the next one already). */
function forgetUser(id?: number) {
  try {
    if (id === undefined || lastUser()?.id === id) localStorage.removeItem(LAST_USER_KEY);
  } catch { /* storage unavailable */ }
}

function sameMe(a: Me, b: Me): boolean {
  return a.id === b.id && a.login === b.login && a.name === b.name && a.avatar_url === b.avatar_url && !!a.operator === !!b.operator;
}

class Session {
  private watching = false;
  private invalidating = false;
  /** Sending the page away after `invalidate`: resolves once it has been (null before). */
  private leaving: Promise<void> | null = null;
  /** The latest focus or page-restore account check (see `accountChecked`). */
  private checking: Promise<void> = Promise.resolve();

  private announce(id: number | null) {
    try { localStorage.setItem(SESSION_SIGNAL, JSON.stringify({ id, nonce: Math.random() })); } catch { /* storage unavailable */ }
  }

  private invalidate = () => {
    if (this.invalidating) return;
    this.invalidating = true;
    // Stop requests and remove rendered private state synchronously; deletion
    // and navigation can finish later, including when another tab is blocked.
    if (this.user) forgetUser(this.user.id);
    this.stopAsking?.();
    selectIdentity(null);
    this.user = null;
    this.status = 'loading';
    beginSignOut();
    this.leaving = clearUserData().finally(() => { clearLocalUserState(); location.assign('/'); });
  };

  private watchAccount() {
    if (this.watching || typeof window === 'undefined') return;
    this.watching = true;
    window.addEventListener('changelist:session-invalidated', this.invalidate);
    window.addEventListener('storage', (event) => {
      if (event.key !== SESSION_SIGNAL || !event.newValue) return;
      try {
        const id: unknown = JSON.parse(event.newValue).id;
        if (id !== (this.user?.id ?? null)) this.invalidate();
      } catch { this.invalidate(); }
    });
    // The check leaves `status` alone: the open view stays mounted, with its
    // unsent text and place, unless the server answers that the account
    // changed (another user, or none), when `invalidate` removes it. A check
    // that fails (offline, a server error) says nothing about the account,
    // so it changes nothing; the next focus asks again. Writes sent
    // meanwhile carry the expected user ID, which the server checks against
    // the cookie's session either way.
    const revalidate = async () => {
      if (this.invalidating || !this.user) return;
      const expected = this.user.id;
      let current: Me | null;
      try { current = await me(); } catch { return; }
      if (current?.id !== expected) {
        this.invalidate();
        await this.leaving;
      }
    };
    window.addEventListener('focus', () => void (this.checking = revalidate()));
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.checking = revalidate(); });
  }

  user: Me | null = $state(null);
  config: AppConfig | null = $state(null);
  /**
   * Whether this deployment offers the bring-your-own-agent features
   * (Remote Relay: pairing agents, sending them threads and questions).
   * Never in the demo, which has no relay.
   */
  get agents(): boolean {
    return !MOCK && !!this.config?.agents;
  }
  status: 'loading' | 'ready' | 'error' = $state('loading');
  /**
   * `user` is the account last signed in on this browser, shown before the
   * server has said who the cookie's session is (status `ready` all the
   * while). Its answer clears this: the same account carries on, another
   * one or nobody replaces it.
   */
  provisional = $state(false);
  /** Stops asking again (`askAgain`) while the server could not say who is signed in; null when not asking. */
  private stopAsking: (() => void) | null = null;
  /** Why the session couldn't load (status `error`); null otherwise. */
  failure: Failure | null = $state(null);
  /**
   * The operator's privacy, terms and support URLs: the server's, or when
   * it can't be read, the last ones it sent to this browser.
   */
  operatorLinks: OperatorLinkUrls | null = $state(rememberedLinks());
  /** Why the last sign-out did not happen (the user is still signed in). */
  signOutError: string | null = $state(null);

  /**
   * Resolves once the latest focus or page-restore account check, or the
   * latest retried check of a remembered account, has decided: at once
   * when it kept the session, or once the page has been sent away when it
   * cleared it. For tests, which have no other signal that a check changed
   * nothing.
   */
  accountChecked(): Promise<void> {
    return this.checking;
  }

  /**
   * Show the remembered account's app now, as if `/api/me` had named it:
   * its identity is selected (so its databases, saved copies and reads are
   * its own), its local state claimed, and the page renders. Its queued
   * review marks are not replayed, and no other tab is told of it, until
   * the server agrees.
   */
  private assume(u: Me) {
    this.provisional = true;
    void setDbUser(u.id);
    claimLocalState(u.id);
    this.user = u;
    this.watchAccount();
    if (typeof location !== 'undefined') dashboardStorage(location.origin);
    this.status = 'ready';
  }

  /**
   * Load the server config and who is signed in. With an account
   * remembered on this browser (and nothing loaded yet), its app is shown
   * first (`assume`), and the answer only confirms it: the same account
   * carries on in place (fields that changed, such as the name, are
   * updated); another account replaces it as an account switch does (a
   * new identity, whose listeners drop the old account's state, its
   * database and local state deleted, and the views mounted again for the
   * new one); nobody shows the sign-in page and forgets it. A 401 to the
   * page's own early reads waits for that answer (`holdSignedOutUntil`).
   * A check that fails keeps the page shown and asks again with backoff
   * (and at once when the browser comes back online); without a page
   * shown, it is the "can't reach" page as before.
   */
  async load() {
    let decided = () => {};
    if (this.status === 'loading' && !this.user && !this.provisional) {
      const kept = lastUser();
      if (kept) {
        this.assume(kept);
        holdSignedOutUntil(new Promise<void>((resolve) => (decided = resolve)));
      }
    }
    try {
      // Independent reads, asked together: the config only words the page.
      const [c, answer] = await Promise.all([
        config().catch(() => null),
        me().then((u) => ({ u }), (e: unknown) => ({ e })),
      ]);
      this.config = c;
      if (c) {
        this.operatorLinks = c.operator_links ?? null;
        rememberLinks(this.operatorLinks);
      }
      let u: Me | null;
      let configured = isConfigured(c);
      if ('e' in answer) {
        const e = answer.e;
        // 503 `not_configured`: the server is not set up yet.
        if (!(e instanceof HttpError && e.status === 503 && (e.body as { error?: string } | null)?.error === 'not_configured')) throw e;
        u = null;
        configured = false;
      } else u = answer.u;
      if (!u) forgetUser();
      if (!u && !configured && !c?.dev_token_login) {
        // Nobody can sign in until the GitHub App is set up: hand over to the
        // server's setup page (a full-page load; status stays 'loading').
        location.assign(SETUP_PATH);
        return;
      }
      this.provisional = false;
      this.stopAsking?.();
      if (u) rememberUser(u);
      // The account is selected, and the local state of any other dropped,
      // before the page renders for it: views mounted for a remembered
      // account that was not this one read nothing of it.
      const selecting = setDbUser(u ? u.id : null);
      decided();
      if (u) claimLocalState(u.id);
      if (!u || !this.user || !sameMe(this.user, u)) this.user = u;
      this.watchAccount();
      this.announce(u?.id ?? null);
      await selecting;
      // From here on, what this page forgets about a pull request goes to the account's invalidation log.
      if (u && typeof location !== 'undefined') dashboardStorage(location.origin);
      this.failure = null;
      this.status = 'ready';
      if (u) void replayPendingMarks();
    } catch (e) {
      decided();
      if (this.provisional) {
        // The remembered account's page stays as it is; its reads say for
        // themselves what fails meanwhile.
        this.askAgain();
        return;
      }
      // A load that fails again leaves the page as it is (no 'loading'
      // flash), with the latest failure.
      this.failure = failureOf(e);
      this.status = 'error';
    }
  }

  /** Ask who is signed in again, with backoff and at once on `online`, until the server answers (see `load`). */
  private askAgain() {
    if (this.stopAsking || typeof window === 'undefined') return;
    const r = new Retrier(async () => {
      await (this.checking = this.load());
      return !this.provisional;
    }, () => {});
    const online = () => r.retryNow();
    window.addEventListener('online', online);
    this.stopAsking = () => {
      r.stop();
      window.removeEventListener('online', online);
      this.stopAsking = null;
    };
    r.start();
  }

  /**
   * End the session and delete this user's cached data from the browser.
   * Queued review marks are sent first; marks that still could not be sent
   * and unsent comment text are deleted only after `confirmLoss` agrees.
   * The data is deleted and the page leaves only once the server has ended
   * the session (a 401 means it already had). Resolves to false when the
   * user stays signed in, with `signOutError` set when the server failed.
   */
  async signOut(confirmLoss: (question: string) => boolean = (q) => confirm(q)): Promise<boolean> {
    this.signOutError = null;
    await settledWithin(flushOpenMarks(), MARKS_FLUSH_WAIT_MS);
    const [marks, drafts] = await Promise.all([idbEntries('marks'), idbEntries('drafts')]);
    const lost: string[] = [];
    if (marks.length) lost.push(`${marks.length} review mark${marks.length === 1 ? '' : 's'} not yet saved`);
    if (drafts.length) lost.push(`${drafts.length} unsent comment${drafts.length === 1 ? '' : 's'}`);
    if (lost.length && !confirmLoss(`Signing out deletes ${lost.join(' and ')} from this browser. Sign out anyway?`))
      return false;
    // Background requests that now get 401 must not start a new sign-in.
    beginSignOut();
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch (e) {
      if (!(e instanceof HttpError && e.status === 401)) {
        cancelSignOut();
        this.signOutError = `Could not sign out: ${e instanceof Error ? e.message : String(e)}`;
        return false;
      }
    }
    forgetUser();
    this.announce(null);
    await clearForSignOut();
    this.user = null;
    selectIdentity(null);
    await clearUserData();
    clearLocalUserState();
    location.assign('/');
    return true;
  }
}

export const session = new Session();
